/**
 * Redux slice backing Account Leadsheet 294. Owns `state.leadsheet`, an object
 * whose root-level keys (`tbData`, `fsData`, `accountSubClassData`,
 * `glAccountData`, `referenceData`, `tickmarkData`) each hold a sibling RTK
 * EntityState bucket. Only one trial balance is loaded at a time, so loading
 * a new TB replaces every bucket via `setAll`.
 *
 * All adapter mutation calls (`setAll`, `setOne`, `upsertOne`, `upsertMany`,
 * `updateOne`, `removeOne`, `getInitialState`) live in this file. The
 * normalizer and row generator hold no adapter mutations; the slice is the
 * single grep-able location for every state write.
 */
import {createSlice} from '@reduxjs/toolkit';
import {leadsheetAdapter, financialStatementAdapter, accountSubClassAdapter, glAccountAdapter, referenceAdapter, tickmarkAdapter} from './LeadsheetAdapter';
import {
	ADD_GLACCOUNT_DOCUMENT,
	BULK_DESIGNATE_IMMATERIAL_GL_ACCOUNTS,
	BULK_RELATE_REFERENCES,
	BULK_REMOVE_IMMATERIAL_GL_ACCOUNTS,
	BULK_REMOVE_GL_ACCOUNT_ANNOTATIONS,
	BULK_UNRELATE_REFERENCES,
	CLEAR_GL_ACCOUNT_TOOLTIP_DOCUMENTS,
	CLEAR_LEADSHEET,
	DELETE_DOCUMENT_BY_ID,
	GET_LEADSHEET_BY_ID,
	PATCH_GL_ACCOUNTS_REMOVE_ALL_DOCUMENTS,
	REFRESH_GL_ACCOUNT_DOCUMENTS,
	REFRESH_GL_ACCOUNT_TAGS,
	RELATE_GL_ACCOUNT_ANNOTATION,
	SET_ENTITY_DOCUMENT_NAME,
	SET_GL_ACCOUNT_TOOLTIP_DOCUMENTS,
	UNRELATE_GL_ACCOUNT_ANNOTATION,
	UPDATE_GL_ACCOUNT_ANNOTATION,
	DELETE_GL_ACCOUNT_ANNOTATION
} from '../../actions/actiontypes';
import {HttpStatusCodes} from '../../util/uiconstants';
import {normalizeLeadsheetResponse} from './apiNormalizer';
import {GL_ACCOUNT_TYPES} from '../../components/Common/CanvasForm/Bodies/BodyTypes/BodySpecial/AccountLeadsheet294/utils/constants';

const WAIVED_OR_IMMATERIAL = [GL_ACCOUNT_TYPES.IMMATERIAL, GL_ACCOUNT_TYPES.WAIVED];
const OK_STATUSES = [HttpStatusCodes.Ok, HttpStatusCodes.Created, HttpStatusCodes.NoContent];

const REFERENCE_GL_FIELD = 'references';
const TICKMARK_GL_FIELD = 'tickmarks';
const REFERENCE_ID_KEY = 'docId';
const TICKMARK_ID_KEY = 'engTagId';

/**
 * Builds the initial leadsheet state with one empty EntityState bucket per adapter.
 * @returns {object}
 */
const getInitialLeadsheetState = () => ({
	tbData: leadsheetAdapter.getInitialState(),
	fsData: financialStatementAdapter.getInitialState(),
	accountSubClassData: accountSubClassAdapter.getInitialState(),
	glAccountData: glAccountAdapter.getInitialState(),
	referenceData: referenceAdapter.getInitialState(),
	tickmarkData: tickmarkAdapter.getInitialState(),
	// Incremented on every GL mutation so body 297 can refetch its metrics.
	leadsheetBodyChangeVersion: 0,
	// Per-GL-account tooltip documents: { [accountId]: Array|null }
	glAccountTooltipDocs: {},
	// Preserved across CLEAR_LEADSHEET so late responses from a previous entity
	// cannot repopulate the newly cleared buckets.
	latestRequestSequence: 0
});

/**
 * True when the response is 200 OK, 201 Created, or 204 NoContent.
 * @param {object} action - API action with `responseObj.status`.
 * @returns {boolean}
 */
const isApiResponseOk = (action) => OK_STATUSES.includes(action?.responseObj?.status);

/**
 * Runs `mutate` against the slice state when the response is OK and the
 * action targets the currently loaded trial balance.
 * @param {object} state - Leadsheet slice draft.
 * @param {object} action - Bulk action with `trialBalanceId`.
 * @param {Function} mutate - Mutation callback receiving the slice draft.
 */
const applyBulkUpdate = (state, action, mutate) => {
	if (!isApiResponseOk(action)) return;
	if (!state.tbData?.entities?.[action.trialBalanceId]) return;
	mutate(state, action);
	state.leadsheetBodyChangeVersion = (state.leadsheetBodyChangeVersion || 0) + 1;
};

const getCurrentTrialBalanceId = (state) => state?.tbData?.ids?.[0];

/**
 * Returns GL ids whose current `glTypeId` is waived/immaterial.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - GL account ids to inspect.
 * @returns {Array}
 */
const filterWaivedOrImmaterialIds = (state, glAccountIds) => {
	const glEntities = state.glAccountData?.entities || {};
	const result = [];
	for (const id of glAccountIds || []) {
		if (WAIVED_OR_IMMATERIAL.includes(glEntities[id]?.glTypeId)) result.push(id);
	}
	return result;
};

/**
 * Returns reference ids currently attached to the supplied GL accounts.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - GL account ids to inspect.
 * @returns {Array}
 */
const collectAttachedReferenceIds = (state, glAccountIds) => {
	const refIds = [];
	const glEntities = state.glAccountData?.entities || {};
	for (const id of glAccountIds || []) {
		const refs = glEntities[id]?.references;
		if (refs?.length) refIds.push(...refs);
	}
	return refIds;
};

/**
 * Returns annotation ids currently attached to the supplied GL accounts.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - GL account ids to inspect.
 * @returns {Array}
 */
const collectAttachedAnnotationIds = (state, glAccountIds) => {
	const annotationIds = [];
	const glEntities = state.glAccountData?.entities || {};
	for (const id of glAccountIds || []) {
		const tickmarks = glEntities[id]?.tickmarks;
		if (tickmarks?.length) annotationIds.push(...tickmarks);
	}
	return annotationIds;
};

const mapActionToReference = (action) => {
	if (action?.reference?.docId !== null && action?.reference?.docId !== undefined) {
		return action.reference;
	}
	if (action?.documentId === null || action?.documentId === undefined) {
		return null;
	}

	const reference = {
		id: action.documentId,
		docId: action.documentId,
		documentId: action.documentId
	};

	if (action?.documentName !== null && action?.documentName !== undefined) {
		reference.docName = action.documentName;
		reference.documentName = action.documentName;
	}
	if (action?.documentFileExt !== null && action?.documentFileExt !== undefined) {
		reference.fileExt = action.documentFileExt;
	}
	if (action?.documentTypeId !== null && action?.documentTypeId !== undefined) {
		reference.docTypeId = action.documentTypeId;
		reference.documentTypeId = action.documentTypeId;
	}
	if (action?.engagementId !== null && action?.engagementId !== undefined) {
		reference.engagementId = action.engagementId;
	}

	return reference;
};

/**
 * Adds child ids to each target GL row and returns the validated items so the
 * slice case handler can run `adapter.upsertMany` against the bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {Array} items - Reference or tickmark objects.
 * @param {string} glField - GL row field that stores the child ids.
 * @param {string} idKey - Item property used as the bucket id.
 * @returns {Array}
 */
function applyBulkChildRelation(state, glAccountIds, items, glField, idKey) {
	if (!state || !glAccountIds?.length || !items?.length) return [];

	const validItems = items.filter((item) => item !== null && item !== undefined && item[idKey] !== null && item[idKey] !== undefined);
	if (validItems.length === 0) return [];

	const newIds = validItems.map((item) => item[idKey]);

	const updates = [];
	for (const glId of glAccountIds) {
		const gl = state.glAccountData?.entities?.[glId];
		if (!gl) continue;
		const current = gl[glField] || [];
		const merged = current.slice();
		let changed = false;
		for (const newId of newIds) {
			if (!merged.includes(newId)) {
				merged.push(newId);
				changed = true;
			}
		}
		if (changed) updates.push({id: glId, changes: {[glField]: merged}});
	}
	if (updates.length > 0 && state.glAccountData) {
		glAccountAdapter.updateMany(state.glAccountData, updates);
	}

	return validItems;
}

/**
 * Attaches references to each target GL account. Returns the validated reference
 * items so the slice case handler can run `referenceAdapter.upsertMany` on the
 * root-level `referenceData` bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {Array} references - Reference objects.
 * @returns {Array}
 */
function applyBulkReferenceRelation(state, glAccountIds = [], references = []) {
	return applyBulkChildRelation(state, glAccountIds, references, REFERENCE_GL_FIELD, REFERENCE_ID_KEY);
}

/**
 * Attaches annotations / tickmarks to each target GL account. Returns the validated
 * tickmark items so the slice case handler can run `tickmarkAdapter.upsertMany`
 * on the root-level `tickmarkData` bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {Array} tickmarks - Tickmark objects.
 * @returns {Array}
 */
function applyBulkAnnotationRelation(state, glAccountIds = [], tickmarks = []) {
	return applyBulkChildRelation(state, glAccountIds, tickmarks, TICKMARK_GL_FIELD, TICKMARK_ID_KEY);
}

/**
 * Removes a single annotation from all GL accounts and the tickmarks bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {string|number} glAccountId - Target GL account id (unused, kept for API compatibility).
 * @param {string|number} engTagId - Engagement tag id to remove.
 */
function applyAnnotationUnrelation(state, glAccountId, engTagId) {
	if (!state || !engTagId) return;

	const glEntities = state.glAccountData?.entities || {};
	const updates = [];
	for (const [glId, gl] of Object.entries(glEntities)) {
		if (!gl?.tickmarks?.length) continue;
		const next = gl.tickmarks.filter((id) => id !== engTagId);
		if (next.length !== gl.tickmarks.length) {
			updates.push({id: glId, changes: {tickmarks: next}});
		}
	}
	if (updates.length > 0 && state.glAccountData) {
		glAccountAdapter.updateMany(state.glAccountData, updates);
	}
	if (state.tickmarkData) {
		tickmarkAdapter.removeOne(state.tickmarkData, engTagId);
	}
}

/**
 * Updates the name and description of a single tickmark in the entity bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {string|number} engTagId - Engagement tag id to update.
 * @param {string} engagementTagName - New tag name.
 * @param {string} engagementTagDescription - New tag description.
 */
function applyAnnotationUpdate(state, engTagId, engagementTagName, engagementTagDescription) {
	if (!state || !engTagId) return;

	if (state.tickmarkData) {
		tickmarkAdapter.updateOne(state.tickmarkData, {
			id: engTagId,
			changes: {engTagName: engagementTagName, engTagDesc: engagementTagDescription}
		});
	}
}

/**
 * Sets the waive/immaterial type for each target GL account.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {number|null} glTypeId - GL type id to apply.
 */
function applyBulkWaiveRelation(state, glAccountIds = [], glTypeId = GL_ACCOUNT_TYPES.WAIVED) {
	if (!state || !glAccountIds?.length) return;

	const updates = [];
	for (const glId of glAccountIds) {
		const gl = state.glAccountData?.entities?.[glId];
		if (!gl) continue;
		if (gl.glTypeId !== glTypeId) updates.push({id: glId, changes: {glTypeId}});
	}
	if (updates.length > 0 && state.glAccountData) {
		glAccountAdapter.updateMany(state.glAccountData, updates);
	}
}

/**
 * Removes reference ids from each target GL account.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {Array} referenceIds - Reference ids to remove.
 */
function applyBulkReferenceUnrelation(state, glAccountIds = [], referenceIds = []) {
	if (!state || !glAccountIds?.length || !referenceIds?.length) return;

	const removals = new Set(referenceIds);
	const updates = [];
	for (const glId of glAccountIds) {
		const gl = state.glAccountData?.entities?.[glId];
		const existing = gl?.references;
		if (!existing?.length) continue;
		const next = existing.filter((id) => !removals.has(id));
		if (next.length !== existing.length) updates.push({id: glId, changes: {references: next}});
	}
	if (updates.length > 0 && state.glAccountData) {
		glAccountAdapter.updateMany(state.glAccountData, updates);
	}
}

/**
 * Renames a reference in the root-level reference entity bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {string|number} documentId - Reference/document id.
 * @param {string} newDocumentName - Updated reference name.
 */
function applyReferenceRename(state, documentId, newDocumentName) {
	if (!state || !documentId || !newDocumentName || !state.referenceData) return;

	const targetId = String(documentId);
	const referenceEntities = state.referenceData.entities || {};
	const directMatch = referenceEntities[targetId] ?? referenceEntities[Number(targetId)];
	if (directMatch) {
		referenceAdapter.updateOne(state.referenceData, {
			id: directMatch.docId,
			changes: {
				docName: newDocumentName,
				documentName: newDocumentName,
				name: newDocumentName
			}
		});
		return;
	}
	const updates = [];

	for (const [entityKey, reference] of Object.entries(referenceEntities)) {
		const isMatch =
			String(entityKey) === targetId ||
			String(reference?.docId) === targetId ||
			String(reference?.id) === targetId ||
			String(reference?.documentId) === targetId;

		if (!isMatch) continue;

		updates.push({
			id: entityKey,
			changes: {
				docName: newDocumentName,
				documentName: newDocumentName,
				name: newDocumentName
			}
		});
	}

	if (updates.length > 0) {
		referenceAdapter.updateMany(state.referenceData, updates);
	}
}

/**
 * Removes a reference id from all GL rows and from the reference entity bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {string|number} documentId - Reference/document id.
 */
function applyReferenceDelete(state, documentId) {
	if (!state || !documentId || !state.referenceData) return;

	const targetId = String(documentId);
	const glEntities = state.glAccountData?.entities || {};
	const targetGlIds = Object.keys(glEntities).filter((glId) => {
		const references = glEntities[glId]?.references;
		return Array.isArray(references) && references.some((referenceId) => String(referenceId) === targetId);
	});

	if (targetGlIds.length > 0) {
		applyBulkReferenceUnrelation(state, targetGlIds, [documentId, targetId]);
	}

	const referenceEntities = state.referenceData.entities || {};
	const referenceEntityKey = Object.keys(referenceEntities).find((entityKey) => {
		const reference = referenceEntities[entityKey];
		return (
			String(entityKey) === targetId ||
			String(reference?.docId) === targetId ||
			String(reference?.id) === targetId ||
			String(reference?.documentId) === targetId
		);
	});

	if (referenceEntityKey !== undefined) {
		referenceAdapter.removeOne(state.referenceData, referenceEntityKey);
	}
}

/**
 * Removes annotation ids from each target GL account and from the tickmarks bucket.
 * @param {object} state - Leadsheet slice draft.
 * @param {Array} glAccountIds - Target GL account ids.
 * @param {Array} annotationIds - Annotation ids to remove.
 */
function applyBulkAnnotationUnrelation(state, glAccountIds = [], annotationIds = []) {
	if (!state || !glAccountIds?.length || !annotationIds?.length) return;

	const removals = new Set(annotationIds);
	const updates = [];
	for (const glId of glAccountIds) {
		const gl = state.glAccountData?.entities?.[glId];
		const existing = gl?.tickmarks;
		if (!existing?.length) continue;
		const next = existing.filter((id) => !removals.has(id));
		if (next.length !== existing.length) updates.push({id: glId, changes: {tickmarks: next}});
	}
	if (updates.length > 0 && state.glAccountData) {
		glAccountAdapter.updateMany(state.glAccountData, updates);
	}
	if (state.tickmarkData && removals.size > 0) {
		// Only remove tickmark entities that are no longer referenced by any GL account.
		const glEntities = state.glAccountData?.entities || {};
		const stillUsed = new Set();
		for (const gl of Object.values(glEntities)) {
			for (const id of gl?.tickmarks || []) stillUsed.add(id);
		}
		const removable = Array.from(removals).filter((id) => !stillUsed.has(id));
		if (removable.length > 0) tickmarkAdapter.removeMany(state.tickmarkData, removable);
	}
}

/**
 * Normalizes the trial balance response and replaces every root-level bucket
 * via the corresponding adapter `setAll` call. Only one trial balance is held
 * at a time, so loading a new TB clears the previous TB and its children.
 * @param {object} state - Leadsheet slice draft.
 * @param {object} action - `GET_LEADSHEET_BY_ID` action.
 */
const handleGetLeadsheetById = (state, action) => {
	const {accountId, requestSequence, requestedPage, trialBalanceId: payloadTrialBalanceId} = action.payload || action;
	if (!action.response || action.responseObj?.status !== HttpStatusCodes.Ok) return;

	const tb = Array.isArray(action.response) ? action.response[0] : action.response;
	if (!tb) return;

	const latestRequestSequence = state.latestRequestSequence || 0;
	if (requestSequence !== undefined && requestSequence <= latestRequestSequence) return;
	const trialBalanceId = payloadTrialBalanceId ?? tb.tbId;
	if (trialBalanceId === null || trialBalanceId === undefined) return;

	const existingSequence = state.tbData?.entities?.[trialBalanceId]?._requestSequence;
	if (requestSequence !== undefined && existingSequence !== undefined && requestSequence < existingSequence) {
		return;
	}

	// Skip applying a page that no longer exists (pages < page, e.g. the total
	// shrank after a filter while the user was on a higher page). The buckets AND
	// the cached metadata (page + totalPages) are left untouched, so the footer
	// keeps reflecting the last valid load that the displayed data belongs to
	// (e.g. stays 2/3 instead of advancing to 3/3). Only flag the paging error and
	// bump the nonce so the data hook surfaces the toast and reverts the local page
	// indicator back to the loaded page. The response never reaches
	// normalizeLeadsheetResponse (apiNormalizer).
	const totalPages = Number(tb.pages) || 0;
	if (requestedPage > 1 && totalPages >= 1 && totalPages < requestedPage) {
		const existingEntity = state.tbData?.entities?.[trialBalanceId];
		leadsheetAdapter.updateOne(state.tbData, {
			id: trialBalanceId,
			changes: {
				// Monotonic nonce so the data hook reconciles on consecutive
				// out-of-range requests; a boolean would not change and the hook
				// effect would not re-run.
				pagingErrorSeq: (existingEntity?.pagingErrorSeq || 0) + 1
			}
		});
		return;
	}

	const {trialBalance, financialStatements, accountSubClasses, glAccounts, references, tickmarks, metadata} = normalizeLeadsheetResponse(tb);

	leadsheetAdapter.setAll(state.tbData, [
		{
			...trialBalance,
			id: trialBalanceId,
			trialBalanceId,
			accountId,
			isCurrentVersion: trialBalance.isCurrentVersion ?? true,
			metadata,
			isLoading: false,
			isLoaded: true,
			error: null,
			pagingErrorSeq: 0,
			_requestSequence: requestSequence
		}
	]);
	if (requestSequence !== undefined) {
		state.latestRequestSequence = Math.max(latestRequestSequence, requestSequence);
	}
	financialStatementAdapter.setAll(state.fsData, financialStatements);
	accountSubClassAdapter.setAll(state.accountSubClassData, accountSubClasses);
	glAccountAdapter.setAll(state.glAccountData, glAccounts);
	referenceAdapter.setAll(state.referenceData, references);
	tickmarkAdapter.setAll(state.tickmarkData, tickmarks);
};

/**
 * Maps a GL-account document item to the slice reference shape keyed by `docId`.
 * Tolerates both the GL-by-id model (`docId`/`docName`/`docTypeId`) and the
 * legacy documents API (`id`/`documentName`/`documentTypeId`).
 * @param {object} doc - Document item from `getGLAccountById` or the documents API.
 * @returns {object|null}
 */
function mapDocumentToReference(doc) {
	const docId = doc?.docId ?? doc?.id;
	if (docId === null || docId === undefined) return null;
	const name = doc.docName ?? doc.documentName ?? doc.name;
	const docTypeId = doc.docTypeId ?? doc.documentTypeId;
	return {
		docId,
		id: docId,
		documentId: docId,
		docName: name,
		documentName: name,
		name,
		fileExt: doc.fileExt,
		docTypeId,
		documentTypeId: docTypeId,
		engagementId: doc.engagementId,
		approvals: Array.isArray(doc.approvals) ? doc.approvals : [],
		isArchived: doc.isArchived
	};
}

/**
 * Maps a tickmark item to the slice tickmark shape keyed by `engTagId`.
 * Tolerates both the GL-by-id model (`engTagId`/`engTagName`/`engTagDesc`/`color`)
 * and the legacy tags API (`engagementTagId`/`engagementTagName`/`engagementTagDescription`).
 * @param {object} tag - Tag item from `getGLAccountById` or the tags API.
 * @returns {object|null}
 */
function mapTagToTickmark(tag) {
	const engTagId = tag?.engTagId ?? tag?.engagementTagId;
	if (engTagId === null || engTagId === undefined) return null;
	const tickmark = {
		engTagId,
		engTagName: tag.engTagName ?? tag.engagementTagName,
		engTagDesc: tag.engTagDesc ?? tag.engagementTagDescription
	};
	if (tag.color !== undefined && tag.color !== null) tickmark.color = tag.color;
	return tickmark;
}

/**
 * Extracts the item array from a read action response (`{data: []}` or array).
 * @param {object} action - Action carrying the API `response`.
 * @returns {Array}
 */
function getRefreshItems(action) {
	const response = action?.response;
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	return [];
}

/**
 * Removes child entities that the supplied GL no longer references and that no
 * other GL still references, by comparing the GL's previous child ids to the
 * union of child ids now used across all GL accounts.
 * @param {object} bucket - `referenceData` or `tickmarkData` EntityState.
 * @param {object} adapter - Matching entity adapter.
 * @param {object} glEntities - `glAccountData.entities`.
 * @param {string} glField - GL row field holding the child ids (`references` / `tickmarks`).
 * @param {Array} previousIds - Child ids the GL held before the refresh.
 */
function pruneOrphanChildEntities(bucket, adapter, glEntities, glField, previousIds) {
	if (!bucket || !previousIds?.length) return;
	const stillUsed = new Set();
	for (const gl of Object.values(glEntities || {})) {
		for (const id of gl?.[glField] || []) stillUsed.add(id);
	}
	const removable = previousIds.filter((id) => !stillUsed.has(id));
	if (removable.length > 0) adapter.removeMany(bucket, removable);
}

/**
 * Fully replaces a single GL account's references from a fresh documents response.
 * @param {object} state - Leadsheet slice draft.
 * @param {object} action - `REFRESH_GL_ACCOUNT_DOCUMENTS` action.
 */
function applyGlDocumentsRefresh(state, action) {
	const glId = action?.glAccountId;
	if (glId === null || glId === undefined) return;
	const gl = state.glAccountData?.entities?.[glId];
	if (!gl) return;

	const previousIds = Array.isArray(gl.references) ? gl.references : [];
	const references = getRefreshItems(action)
		.map(mapDocumentToReference)
		.filter((ref) => ref?.isArchived);
	const newIds = references.map((ref) => ref.docId);

	if (references.length > 0 && state.referenceData) referenceAdapter.upsertMany(state.referenceData, references);
	const changes = {references: newIds};
	if (action.glTypeId !== undefined) changes.glTypeId = action.glTypeId;
	glAccountAdapter.updateOne(state.glAccountData, {id: glId, changes});
	pruneOrphanChildEntities(state.referenceData, referenceAdapter, state.glAccountData?.entities, REFERENCE_GL_FIELD, previousIds);
}

/**
 * Fully replaces a single GL account's tickmarks from a fresh tags response.
 * @param {object} state - Leadsheet slice draft.
 * @param {object} action - `REFRESH_GL_ACCOUNT_TAGS` action.
 */
function applyGlTagsRefresh(state, action) {
	const glId = action?.glAccountId;
	if (glId === null || glId === undefined) return;
	const gl = state.glAccountData?.entities?.[glId];
	if (!gl) return;

	const previousIds = Array.isArray(gl.tickmarks) ? gl.tickmarks : [];
	const tickmarks = getRefreshItems(action).map(mapTagToTickmark).filter(Boolean);
	const newIds = tickmarks.map((tm) => tm.engTagId);

	if (tickmarks.length > 0 && state.tickmarkData) tickmarkAdapter.upsertMany(state.tickmarkData, tickmarks);
	const changes = {tickmarks: newIds};
	if (action.glTypeId !== undefined) changes.glTypeId = action.glTypeId;
	glAccountAdapter.updateOne(state.glAccountData, {id: glId, changes});
	pruneOrphanChildEntities(state.tickmarkData, tickmarkAdapter, state.glAccountData?.entities, TICKMARK_GL_FIELD, previousIds);
}

/** Slice with extra-reducers for trial balance load and the bulk mutation actions. */
export const leadsheetSlice = createSlice({
	name: 'leadsheet',
	initialState: getInitialLeadsheetState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(CLEAR_LEADSHEET, (state, action) => ({
			...getInitialLeadsheetState(),
			latestRequestSequence: action.requestSequence ?? 0
		}));
		builder.addCase(ADD_GLACCOUNT_DOCUMENT, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const reference = mapActionToReference(action);
				if (!reference) return;
				const waivedIds = filterWaivedOrImmaterialIds(s, action.glAccountIds);
				const newRefs = applyBulkReferenceRelation(s, action.glAccountIds, [reference]);
				if (newRefs.length > 0 && s.referenceData) {
					referenceAdapter.upsertMany(s.referenceData, newRefs);
				}
				if (waivedIds.length > 0) applyBulkWaiveRelation(s, waivedIds, null);
			});
		});
		builder.addCase(BULK_RELATE_REFERENCES, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const waivedIds = filterWaivedOrImmaterialIds(s, action.glAccountIds);
				const newRefs = applyBulkReferenceRelation(s, action.glAccountIds, action.references);
				if (newRefs.length > 0 && s.referenceData) {
					referenceAdapter.upsertMany(s.referenceData, newRefs);
				}
				if (waivedIds.length > 0) applyBulkWaiveRelation(s, waivedIds, null);
			});
		});
		builder.addCase(BULK_UNRELATE_REFERENCES, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				applyBulkReferenceUnrelation(s, action.glAccountIds, action.referenceIds);
			});
		});
		builder.addCase(BULK_DESIGNATE_IMMATERIAL_GL_ACCOUNTS, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const refIds = collectAttachedReferenceIds(s, action.glAccountIds);
				applyBulkWaiveRelation(s, action.glAccountIds, action.glTypeId);
				if (refIds.length > 0) applyBulkReferenceUnrelation(s, action.glAccountIds, refIds);
			});
		});
		builder.addCase(BULK_REMOVE_IMMATERIAL_GL_ACCOUNTS, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				applyBulkWaiveRelation(s, action.glAccountIds, action.glTypeId);
			});
		});
		builder.addCase(PATCH_GL_ACCOUNTS_REMOVE_ALL_DOCUMENTS, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const refIds = collectAttachedReferenceIds(s, action.glAccountIds);
				if (refIds.length > 0) applyBulkReferenceUnrelation(s, action.glAccountIds, refIds);
			});
		});
		builder.addCase(RELATE_GL_ACCOUNT_ANNOTATION, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const newTickmarks = applyBulkAnnotationRelation(s, action.glAccountIds, [
					{
						engTagId: action.engTagId,
						engTagName: action.engagementTagName,
						engTagDesc: action.engagementTagDescription
					}
				]);
				if (newTickmarks.length > 0 && s.tickmarkData) {
					tickmarkAdapter.upsertMany(s.tickmarkData, newTickmarks);
				}
			});
		});
		builder.addCase(UPDATE_GL_ACCOUNT_ANNOTATION, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				applyAnnotationUpdate(s, action.engTagId, action.engagementTagName, action.engagementTagDescription);
			});
		});
		builder.addCase(UNRELATE_GL_ACCOUNT_ANNOTATION, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				applyBulkAnnotationUnrelation(s, action.glAccountIds, [action.engTagId]);
			});
		});
		builder.addCase(DELETE_GL_ACCOUNT_ANNOTATION, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				applyAnnotationUnrelation(s, action.glAccountId, action.engTagId);
			});
		});
		builder.addCase(SET_ENTITY_DOCUMENT_NAME, (state, action) => {
			const trialBalanceId = action.trialBalanceId ?? getCurrentTrialBalanceId(state);
			if (!trialBalanceId) return;

			applyBulkUpdate(
				state,
				{
					...action,
					trialBalanceId,
					responseObj: action.responseObj ?? {status: HttpStatusCodes.Ok}
				},
				(s, a) => {
					applyReferenceRename(s, a.documentId, a.newDocumentName);
				}
			);
		});
		builder.addCase(DELETE_DOCUMENT_BY_ID, (state, action) => {
			const trialBalanceId = action.trialBalanceId ?? getCurrentTrialBalanceId(state);
			if (!trialBalanceId) return;

			applyBulkUpdate(
				state,
				{
					...action,
					trialBalanceId,
					responseObj: action.responseObj ?? {status: HttpStatusCodes.Ok}
				},
				(s, a) => {
					applyReferenceDelete(s, a.documentId);
				}
			);
		});
		builder.addCase(BULK_REMOVE_GL_ACCOUNT_ANNOTATIONS, (state, action) => {
			applyBulkUpdate(state, action, (s) => {
				const annotationIds = collectAttachedAnnotationIds(s, action.glAccountIds);
				if (annotationIds.length > 0) applyBulkAnnotationUnrelation(s, action.glAccountIds, annotationIds);
			});
		});
		builder.addCase(REFRESH_GL_ACCOUNT_DOCUMENTS, (state, action) => {
			applyGlDocumentsRefresh(state, action);
		});
		builder.addCase(REFRESH_GL_ACCOUNT_TAGS, (state, action) => {
			applyGlTagsRefresh(state, action);
		});
		builder.addCase(SET_GL_ACCOUNT_TOOLTIP_DOCUMENTS, (state, action) => {
			const {accountId, documents} = action;
			if (accountId === null || accountId === undefined) return;
			if (!state.glAccountTooltipDocs) state.glAccountTooltipDocs = {};
			state.glAccountTooltipDocs[accountId] = documents;
		});
		builder.addCase(CLEAR_GL_ACCOUNT_TOOLTIP_DOCUMENTS, (state, action) => {
			const {accountId} = action;
			if (accountId === null || accountId === undefined) return;
			if (!state.glAccountTooltipDocs) return;
			delete state.glAccountTooltipDocs[accountId];
		});
		builder.addCase(GET_LEADSHEET_BY_ID, handleGetLeadsheetById);
	}
});

/** Reducer registered under `state.leadsheet`. */
export const leadsheetReducer = leadsheetSlice.reducer;
